import React from 'react';

import { Bubble, Button, Checkbox } from '@lt/components';
import { i18n } from '@lt/localization';
import styled from 'styled-components';
import { removeSurrogatePairAndReplaceEmojiToText } from '../../utils/removeSurrogatePairAndReplaceEmojiToText';
import TextField from '../../../../components/TextField';
import type { ICheckbox } from '../../CheckoutRoutes/PackageCheckout/containers/Services/types';

export interface ReduxCommentProps {
  text: string;
  checkboxes: ICheckbox[];
}

interface Props {
  isHotelOnly: boolean;
  reduxComment: ReduxCommentProps;
  submit: (result: ReduxCommentProps) => void;
  isMobile: boolean;
}

interface State {
  value: string;
  checkboxes: ICheckbox[];
  isVisible: boolean;
  isEditing: boolean;
  isOpen: boolean;
  hoverOnButton: boolean;
}

class ExtraComment extends React.PureComponent<Props, State> {
  /**
   * @return {boolean}
   */
  static isCommentExist(commentObject) {
    const { text, checkboxes } = commentObject;
    return Boolean(
      text || checkboxes.filter(({ checked }) => Boolean(checked)).length,
    );
  }

  textareaRef: React.RefObject<HTMLTextAreaElement>;

  constructor(props) {
    super(props);

    const { reduxComment } = this.props;
    const { text, checkboxes } = reduxComment || {};
    const isCommentExist = ExtraComment.isCommentExist(reduxComment);

    this.state = {
      isVisible: isCommentExist,
      isEditing: !isCommentExist,
      isOpen: false,
      hoverOnButton: false,
      value: text,
      checkboxes,
    };

    this.toggleEdit = this.toggleEdit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.toggleCommentForm = this.toggleCommentForm.bind(this);

    this.textareaRef = React.createRef();
  }

  handleChange(e) {
    const str = removeSurrogatePairAndReplaceEmojiToText(e.target.value);
    this.setState({ value: str });
  }

  handleSave(e) {
    const { submit } = this.props;
    const { value, checkboxes } = this.state;

    e.preventDefault();

    const result = {
      text: value,
      checkboxes,
    };

    submit(result);

    if (!ExtraComment.isCommentExist(result)) {
      this.toggleCommentForm();
      return;
    }

    this.toggleEdit();
    this.setState({ isOpen: false });
    this.setState({ hoverOnButton: false });
  }

  handleChangeCheckbox(text) {
    const { checkboxes } = this.state;
    this.setState({
      checkboxes: checkboxes.map((item) => {
        if (item.text === text) {
          const result = {
            ...item,
            checked: !item.checked,
          };

          if (item.input) {
            return {
              ...result,
              input: {
                ...item.input,
                value: '',
              },
            };
          }

          return result;
        }
        return item;
      }),
    });
  }

  handleChangeCheckboxInput({ text, value }) {
    const { checkboxes } = this.state;
    this.setState({
      checkboxes: checkboxes.map((item) => {
        if (item.text === text) {
          return {
            ...item,
            input: {
              ...item.input,
              value,
            },
          };
        }
        return item;
      }),
    });
  }

  onMouseEnter = () => {
    this.setState({ hoverOnButton: true });
  };

  onMouseLeave = () => {
    this.setState({ hoverOnButton: false });
  };

  onBlurHandler = () => {
    const { hoverOnButton } = this.state;
    if (!hoverOnButton) this.setState({ isOpen: false });
  };

  onFocusHandler = () => {
    this.setState({ isOpen: true });
  };

  toggleEdit() {
    const { isEditing } = this.state;
    this.setState({ isEditing: !isEditing });
  }

  toggleCommentForm() {
    const { isVisible } = this.state;
    this.setState({ isVisible: !isVisible });
  }

  render() {
    const { reduxComment, isHotelOnly, isMobile } = this.props;
    const { isVisible, value, isEditing, checkboxes, isOpen } = this.state;

    const buttonText = i18n.t('checkout.extraComment.button', {
      context: isEditing ? 'save' : 'edit',
    });

    if (isHotelOnly) return null;

    return (
      <StyledExtraComment isMobile={isMobile}>
        <StyledTitle>{i18n.t('checkout.extraComment.title')}</StyledTitle>

        {!isVisible && (
          <StyledContainer isMobile={isMobile}>
            <StyledOpenFormButton size="flex" onClick={this.toggleCommentForm}>
              {i18n.t('checkout.extraComment.openForm.button')}
            </StyledOpenFormButton>

            <span>{i18n.t('checkout.extraComment.openForm.description')}?</span>
          </StyledContainer>
        )}

        {isVisible && (
          <Content>
            {isEditing && (
              <div>
                <Description>
                  {i18n.t('checkout.extraComment.description', {
                    context: isHotelOnly ? 'hotel' : 'package',
                  })}
                </Description>

                <Checkboxes isMobile={isMobile}>
                  {checkboxes.map(({ text, checked, tooltip, input }) => (
                    <CheckboxItem
                      isMobile={isMobile}
                      hasInput={!!input}
                      key={text}
                    >
                      <Checkbox
                        className="checkout-checkbox"
                        checked={checked}
                        onChange={() => this.handleChangeCheckbox(text)}
                      >
                        {text}
                        {tooltip && (
                          <Bubble text={tooltip}>
                            <div />
                          </Bubble>
                        )}
                      </Checkbox>
                      {input && checked && (
                        <StyledTextField
                          value={input.value}
                          placeholder={input.placeholder}
                          onChange={({ target }) =>
                            this.handleChangeCheckboxInput({
                              text,
                              value: target.value,
                            })
                          }
                          helperText={input.remark}
                          isMobile={isMobile}
                        />
                      )}
                    </CheckboxItem>
                  ))}
                </Checkboxes>

                <StyledTextArea
                  id="order_comment"
                  value={value}
                  onChange={this.handleChange}
                  onBlur={this.onBlurHandler}
                  onFocus={this.onFocusHandler}
                  placeholder={i18n.t('checkout.extraComment.comment')}
                  ref={this.textareaRef}
                  isMobile={isMobile}
                  isOpen={isOpen}
                />
              </div>
            )}

            {!isEditing && ExtraComment.isCommentExist(reduxComment) && (
              <>
                {reduxComment.checkboxes.map(({ text, input, checked }) =>
                  checked ? (
                    <Styledtext key={text}>
                      {text}
                      {input && input.value ? (
                        <>
                          &nbsp;
                          <strong>{`№ ${input.value}`}</strong>
                        </>
                      ) : null}
                    </Styledtext>
                  ) : null,
                )}
                <Styledtext>{reduxComment.text}</Styledtext>
              </>
            )}

            <StyledSaveButton
              primary
              onClick={this.handleSave}
              onMouseEnter={this.onMouseEnter}
              onMouseLeave={this.onMouseLeave}
              functional={!isEditing}
              isMobile={isMobile}
              isEditing={isEditing}
            >
              {buttonText}
            </StyledSaveButton>
          </Content>
        )}
      </StyledExtraComment>
    );
  }
}

const StyledExtraComment = styled.div<{ isMobile: boolean }>`
  color: #919499;
  font-size: ${({ isMobile }) => (isMobile ? '13' : '14')}px;
`;

const StyledTitle = styled.h2`
  margin-bottom: 15px;
  margin-left: 10px;
  color: #17181a;
`;
const StyledContainer = styled.div<{ isMobile: boolean }>`
  span {
    ${({ isMobile }) => isMobile && 'margin-top: 15px;'}
    display: inline-block;
  }
  ${({ isMobile }) => isMobile && `margin: 0 10px;`}
`;

const StyledOpenFormButton = styled(Button)`
  margin-right: 12px;
  width: 220px;
  font-size: 14px;
`;

const Content = styled.div`
  padding: 13px 15px 21px;
  color: #4d4d4d;
  background-color: #fff;
  border-radius: 6px;
`;

const Description = styled.p`
  margin-bottom: 18px;
  color: #919499;
`;

const Checkboxes = styled.ul<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
`;

const CheckboxItem = styled.li<{ isMobile: boolean; hasInput: boolean }>`
  margin-bottom: ${({ isMobile }) => (isMobile ? '16' : '10')}px;
  flex-basis: ${({ hasInput }) => (hasInput ? '100' : '50')}%;
  font-size: ${({ isMobile }) => (isMobile ? '13' : '14')}px;
`;

const StyledTextField = styled(TextField)`
  margin-top: 10px;
  margin-bottom: 0;
  width: 100%;
`;

const StyledTextArea = styled.textarea<{ isMobile: boolean; isOpen: boolean }>`
  padding: 8px 10px;
  box-sizing: border-box;
  width: 100%;
  height: 50px;
  font-size: ${({ isMobile }) => (isMobile ? '13' : '14')}px;
  resize: none;
  border: 1px solid #c2c5cc;
  border-radius: 4px;
  transition: height 200ms ease;
  &::placeholder {
    color: #c2c5cc;
  }
  ${({ isOpen }) => isOpen && 'height: 120px;'}
`;

const Styledtext = styled.p`
  margin-top: 10px;
`;

const StyledSaveButton = styled(Button)<{
  isMobile: boolean;
  isEditing: boolean;
}>`
  margin-top: 10px;
  display: block;
  height: 50px;
  font-size: ${({ isMobile }) => (isMobile ? '13' : '14')}px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '194px')};
  ${({ isEditing }) =>
    !isEditing &&
    `
    margin-bottom: ${({ isMobile }) => (isMobile ? '0' : '6')}px;
    width: auto !important;
    height: auto;
    color: #007aff;
  `}
`;

export default ExtraComment;
